/**
 * Trunnion AI - Page Error Boundary
 * Thin wrapper around ErrorBoundary for use at the page/route level.
 * Provides automatic reset capability using a key-based reset pattern
 * tied to the current URL location.
 */

import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from './ErrorBoundary';

interface PageErrorBoundaryProps {
  children: ReactNode;
}

/**
 * PageErrorBoundary wraps page-level content with an ErrorBoundary that
 * automatically resets when the route changes. The "Try Again" button in
 * the fallback resets the boundary by forcing a key change, which unmounts
 * and remounts the ErrorBoundary, clearing the error state.
 */
export function PageErrorBoundary({ children }: PageErrorBoundaryProps) {
  const location = useLocation();

  // Use the full pathname + search as the reset key.
  // When the user navigates away and comes back, the error boundary
  // automatically resets because the key changes.
  const resetKey = location.pathname + location.search;

  return (
    <ErrorBoundary
      resetKey={resetKey}
      onError={(error, errorInfo) => {
        console.error(
          '[Trunnion AI] Page error boundary caught:',
          error.message,
          '\nComponent stack:',
          errorInfo.componentStack,
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
